// Angular imports
import { Component, OnInit, Input, ViewChild, ViewChildren, QueryList, Inject } from '@angular/core';

// 3rd party imports
import { custom } from 'devextreme/ui/dialog';
import { deserialize } from 'class-transformer';
import { dxElement } from 'devextreme/core/element';
import { DxBoxComponent, DxContextMenuComponent } from 'devextreme-angular';

// Common imports
import { CCLocalStorageService, CCLocalStorageCategory, Utils } from 'cc-common';

// Component imports
import { MultiColumnSelectorComponent } from '../../management-common/component/common/multi-column-selector/multi-column-selector.component';
import { DynamicDashboardComponent } from '../dynamic-dashboard/dynamic-dashboard.component';

// Service imports
import { ScreenService } from '../../_services/shared-services/screen.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { UserService } from '../../_services/data-services/user.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { ContextManagerService } from '../../_services/data-services/context-manager.service';
import { CommunityService } from '../../_services/data-services/community.service';
import { DynamicDashboardService } from '../_services/dynamic-dashboard.service';
import { EntityService } from '../../cirabooks/corporate-cirabooks-module/_services/entity.service';
import { CCBHelperService } from '../../cirabooks/shared-cirabooks-module/_services/shared-services/ccb-helper.service';

// Model imports
import { DashboardViewMode, DynamicDashboardEntry } from '../_model/dynamic-dashboard-entry';
import { PropertyCommunityInfoResultDto } from '../../_models/property-community-info-result-dto';
import { CommunitySelectorResultDto } from '../../_models/community-selector-result-dto';
import { SharedWorkspaceResultDto } from '../_model/shared-workspace-result-dto';
import { SystemDashboardsFactory } from '../_interfaces/system-dashboards-factory';
import { EntitySelectorResultDto } from '../../cirabooks/shared-cirabooks-module/_models/common/enity-selector-result-dto';

// Constant imports
import { StorageKeyConstants } from '../../_constants/storage-key-constants';
import { Constants } from '../../_constants/constants';

@Component({
    selector: 'app-dashboard-viewer',
    templateUrl: './dashboard-viewer.component.html',
    styleUrls: ['./dashboard-viewer.component.scss']
})
export class DashboardViewerComponent implements OnInit {
    @ViewChild('configMenu', { static: false }) configMenu: DxContextMenuComponent;
    @ViewChild('communityMultiSelector', { static: false }) communityMultiSelector: MultiColumnSelectorComponent;
    @ViewChild('dashboardBox', { static: false }) dashboardBox: DxBoxComponent;
    @ViewChildren(DynamicDashboardComponent) dashboardTemplates: QueryList<DynamicDashboardComponent>;

    @Input() pageTitle: string;
    @Input() pageHelpLink: string;

    configVisible: boolean;
    sharedWorkspaceVisible: boolean;
    loadingVisible: boolean;
    loadingComplete: boolean;
    isFullScreen: boolean;
    hasDashboards: boolean;
    hasSystemDashboards: boolean;
    hasUserDashboards: boolean;
    isITUser: boolean;
    isInternalUser: boolean;

    selectedContext: PropertyCommunityInfoResultDto;
    communities: Array<CommunitySelectorResultDto>;
    entityList: Array<EntitySelectorResultDto>;
    selectedEntity: EntitySelectorResultDto;
    selectedCommunity: CommunitySelectorResultDto;
    selectedCommunityId: number;
    entityValue: number;

    systemDashboards: Array<DynamicDashboardEntry>;
    userDashboards: Array<DynamicDashboardEntry>;
    selectedDashboard: DynamicDashboardEntry;
    currentDashboard: DynamicDashboardEntry;
    forceRefresh = false;

    communityColumns: any[];
    communityGridBoxValue: number[];
    multiViewIndex = 0;
    totalWidth = -1;
    totalHeight = -1;
    viewerWidth = -1;
    viewerHeight = -1;
    showScrollbar = false;
    scrollDirection = 'both';
    scrollbarPreference = Constants.defaultScrollbarView;

    showRenameWorkspacePopup = false;
    isCorporateCiraBook = false;
    workspaceName: string;

    private _timerId: any;
    private readonly _defaultCustomWorkspaceMessage = 'Do you want to save your changes as default custom workspace?';

    constructor(
        private readonly entityService: EntityService,
        private readonly screenService: ScreenService,
        private readonly helperService: HelperService,
        private readonly userService: UserService,
        private readonly errorService: ServerErrorService,
        private readonly contextService: ContextManagerService,
        private readonly communityService: CommunityService,
        private readonly localStorageService: CCLocalStorageService,
        private readonly dashboardService: DynamicDashboardService,
        private readonly ccbHelperService: CCBHelperService,
        @Inject('DashboardsFactory') private readonly dashboardsFactory: SystemDashboardsFactory
    ) {
        this.isITUser = this.userService.isITUser();
        this.isInternalUser = this.userService.isInternal();
        this.onSharedWorkspace = this.onSharedWorkspace.bind(this);
        this.onConfigureNew = this.onConfigureNew.bind(this);
    }

    ngOnInit(): void {
        const dashboardType = this.dashboardsFactory.getDashboardCategoryCode();
        this.isCorporateCiraBook = Utils.equalsIgnoreCase(dashboardType, 'CCB');
        this.communityColumns = this.communityService.getCommunitySelectorColumns();
        this.selectedContext = this.contextService.getSelectedContext();
        if (!this.selectedContext) {
            this.getCommunities();
        }
        if (Utils.isNullOrUndefined(this.selectedEntity)) {
            this.getEntities();
        }

        this.helperService.subjectSideNavSizeChanged.subscribe(() => {
            setTimeout(() => this.computeSectionsSize(), 250);
        });
        this.screenService.changed.subscribe(() => {
            setTimeout(() => this.computeSectionsSize(), 250);
        });

        this.contextService.getContext().subscribe(() => {
            this.selectedContext = this.contextService.getSelectedContext();
            this.onCommunityChange();
        });

        this.dashboardService.getLocalContext().subscribe(() => {
            const localContext = this.dashboardService.getLocalContextInfo();
            if (!!localContext && !!localContext.communityId) {
                this.communityGridBoxValue = [localContext.communityId];
            }
        });

        this.dashboardService.getDashboardWidgets().subscribe(() => {
            this.loadingComplete = true;
            this.populateUserDashboards();
        });
    }

    onSharedWorkspace(): void {
        if (!!this.configMenu && this.configMenu.instance) {
            this.configMenu.instance.hide();
        }
        this.sharedWorkspaceVisible = true;
    }

    onImportSharedWorkspace(rec: SharedWorkspaceResultDto): void {
        this.sharedWorkspaceVisible = false;
        const dashboard = DynamicDashboardEntry.fromSharedWorkspace(rec);
        dashboard.isDefault = false;
        dashboard.isShared = false;
        dashboard.isSystem = false;
        dashboard.name = `${rec.name} shared by ${rec.createdBy.displayName}`;
        dashboard.id = this.dashboardService.getNewDashboardId();
        this.onEdit(dashboard);
    }

    onEditSharedWorkspace(rec: SharedWorkspaceResultDto): void {
        this.sharedWorkspaceVisible = false;
        let dashboard = this.systemDashboards
            .concat(this.userDashboards)
            .find((r) => !!r.sharedWorkspace && r.sharedWorkspace.key === rec.key);
        if (!dashboard) {
            dashboard = DynamicDashboardEntry.fromSharedWorkspace(rec);
        }
        this.onEdit(dashboard);
    }

    onDeleteSharedWorkspace(rec: SharedWorkspaceResultDto): void {
        this.sharedWorkspaceVisible = false;
        let dashboard = this.systemDashboards
            .concat(this.userDashboards)
            .find((r) => !!r.sharedWorkspace && r.sharedWorkspace.key === rec.key);
        if (!dashboard) {
            dashboard = DynamicDashboardEntry.fromSharedWorkspace(rec);
        }
        this.onDelete(dashboard);
    }

    onCloseSharedWorkspace(): void {
        this.sharedWorkspaceVisible = false;
    }

    onConfigureNew(): void {
        if (!!this.configMenu && this.configMenu.instance) {
            this.configMenu.instance.hide();
        }
        if (this.dashboardService.hasNoConfigConflict()) {
            this.selectedDashboard = new DynamicDashboardEntry();
            this.selectedDashboard.id = this.dashboardService.getNewDashboardId();
            this.configVisible = true;
        }
    }

    onViewByName(name: string): void {
        if (Utils.notNullOrEmpty(this.systemDashboards) && Utils.notNullOrEmpty(name)) {
            const dashboard = this.systemDashboards
                .concat(this.userDashboards)
                .find((r) => Utils.equalsIgnoreCase(r.name, name));
            if (Utils.notNullAndDefined(dashboard)) {
                this.onView(dashboard);
            }
        }
    }

    onViewerReady(): void {
        this.computeSectionsSize();
    }

    onView(dashboard: DynamicDashboardEntry): void {
        if (!!this.configMenu && !!this.configMenu.instance) {
            this.configMenu.instance.hide();
            setTimeout(() => this.dashboardService.selectionChanged.next(dashboard), 10);
        }
        if (!this.currentDashboard || this.currentDashboard.id !== dashboard.id || this.forceRefresh) {
            this.forceRefresh = false;
            this.currentDashboard = null;
            this.multiViewIndex = 0;
            setTimeout(() => {
                this.currentDashboard = dashboard;
                this.computeSectionsSize();
                this.onCommunityChange();
            }, 10);
        }
    }

    onMarkDefault(dashboard: DynamicDashboardEntry): void {
        this.dashboardService.markDefaultDashboard(dashboard).subscribe(
            () => {
                this.reloadDashboards(dashboard);
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    onEdit(dashboard: DynamicDashboardEntry): void {
        if (!!this.configMenu && this.configMenu.instance) {
            this.configMenu.instance.hide();
        }
        this.selectedDashboard = deserialize(DynamicDashboardEntry, dashboard.toStorageString());
        this.configVisible = true;
    }

    onDelete(dashboard: DynamicDashboardEntry): void {
        if (!!this.configMenu && this.configMenu.instance) {
            this.configMenu.instance.hide();
        }

        const dialog = custom({
            title: 'Confirmation',
            messageHtml: `Are you sure you want to delete this Workspace?`,
            buttons: [
                {
                    text: 'Yes - Shared & Mine',
                    type: 'danger',
                    visible: dashboard.isShared,
                    onClick: (e) => 'BOTH'
                },
                {
                    text: dashboard.isShared ? 'Yes - Only Mine' : 'Yes',
                    type: 'danger',
                    onClick: (e) => 'MINE'
                },
                {
                    text: 'No',
                    type: 'default',
                    onClick: (e) => 'CANCEL'
                }
            ]
        });
        dialog.show().then((answer: string) => {
            if (answer !== 'CANCEL') {
                this.dashboardService.deleteDashboard(dashboard, Utils.equalsIgnoreCase(answer, 'BOTH')).subscribe(
                    () => {
                        this.reloadDashboards(dashboard);
                    },
                    (error) => {
                        this.errorService.displayError(error);
                    }
                );
            }
        });
    }

    onSave(dashboard: DynamicDashboardEntry): void {
        this.dashboardService.saveDashboard(dashboard).subscribe(
            () => {
                this.reloadDashboards(dashboard);
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    onCancel(e): void {
        this.configVisible = false;
        this.selectedDashboard = null;
    }

    onUpdateWidget(): void {
        if (Utils.isFalse(this.isITUser) && Utils.isTrue(this.currentDashboard.isSystem)) {
            this.showRenameWorkspace();
        } else {
            const dashboard: DynamicDashboardEntry = deserialize(
                DynamicDashboardEntry,
                this.currentDashboard.toStorageString()
            );
            this.dashboardService.saveDashboard(dashboard).subscribe(
                () => {
                    this.currentDashboard = null;
                    this.populateUserDashboards();
                    this.onView(dashboard);
                },
                (error) => {
                    this.errorService.displayError(error);
                }
            );
        }
    }

    onResizeWidget(): void {
        if (Utils.isFalse(this.isITUser) && Utils.isTrue(this.currentDashboard.isSystem)) {
            this.showRenameWorkspace();
        } else {
            const dashboard: DynamicDashboardEntry = deserialize(
                DynamicDashboardEntry,
                this.currentDashboard.toStorageString()
            );
            this.dashboardService.saveDashboard(dashboard).subscribe(
                () => {
                    this.populateUserDashboards();
                },
                (error) => {
                    this.errorService.displayError(error);
                }
            );
        }
    }

    onResetLayout(): void {
        if (Utils.isFalse(this.isITUser) && Utils.isTrue(this.currentDashboard.isSystem)) {
            this.showRenameWorkspace();
        } else {
            const dashboard: DynamicDashboardEntry = deserialize(
                DynamicDashboardEntry,
                this.currentDashboard.toStorageString()
            );
            dashboard.resetRatios();
            this.currentDashboard = null;
            this.dashboardService.saveDashboard(dashboard).subscribe(
                () => {
                    setTimeout(() => {
                        this.currentDashboard = dashboard;
                        this.populateUserDashboards();
                        this.onCommunityChange();
                    }, 10);
                },
                (error) => {
                    this.errorService.displayError(error);
                }
            );
        }
    }

    onFullScreen(isFullScreen: boolean): void {
        this.isFullScreen = isFullScreen;
        this.dashboardService.isFullScreenWidget.next(this.isFullScreen);
    }

    onCommunitySelection(selectedObject: any): void {
        if (!!selectedObject && selectedObject.selectedRow.length > 0) {
            const selectedRows: any[] = selectedObject.selectedRow;
            if (!this.selectedCommunity || this.selectedCommunity.communityId !== selectedRows[0].communityId) {
                this.selectedCommunity = selectedRows[0];
                this.dashboardService.updateLocalContext(this.selectedCommunity);
                // ANG-2718, Avoid recursive call for the method
                this.onCommunityChange();
            }
        } else {
            this.selectedCommunity = null;
            this.communityMultiSelector.resetSelector();
            this.dashboardService.updateLocalContext(null);
            // ANG-2718, Avoid recursive call for the method
            this.onCommunityChange();
        }
    }

    onEntitySelection(): void {
        this.selectedEntity = this.entityList.find((e) => Utils.equals(this.entityValue, e.entityId));
        this.ccbHelperService.updateSelectedEntitySubject(this.selectedEntity);
        this.onEntityChange();
    }

    onCarouselViewNavigationClick(index: number): void {
        this.multiViewIndex = index;
    }

    onAutoFitView(): void {
        this.currentDashboard.resetRatios();
        this.onResizeWidget();
        this.computeSectionsSize();
    }

    maximumOf(num1: number, num2: number, autoFitNum: number): number {
        if (this.currentDashboard.autoFitView) {
            return autoFitNum;
        }
        return Math.max(num1, num2);
    }

    onRenameWorkspaceClose(workspaceName: string): void {
        this.showRenameWorkspacePopup = false;
        if (Utils.notNullAndDefined(workspaceName)) {
            const dashboard = new DynamicDashboardEntry();
            dashboard.id = this.dashboardService.getNewDashboardId();
            dashboard.isDefault = true;
            dashboard.isShared = false;
            dashboard.isSystem = false;
            dashboard.name = workspaceName;
            dashboard.sharedWorkspace = null;
            dashboard.allowExternalUsers = false;
            dashboard.noOfSections = this.currentDashboard.noOfSections;
            dashboard.viewMode = this.currentDashboard.viewMode;
            dashboard.dashboardSections = this.currentDashboard.dashboardSections;

            this.dashboardService.saveDashboard(dashboard).subscribe(
                () => {
                    this.currentDashboard = dashboard;
                    this.onMarkDefault(dashboard);
                },
                (error) => {
                    this.errorService.displayError(error);
                }
            );
        }
    }

    private showRenameWorkspace(): void {
        const dialog = this.helperService.getConfirmationDialog('Confirmation', this._defaultCustomWorkspaceMessage);
        dialog.show().then((answer: boolean) => {
            if (Utils.isTrue(answer)) {
                this.workspaceName = `Custom_${this.currentDashboard.name}`;
                this.showRenameWorkspacePopup = true;
            }
        });
    }

    private reloadDashboards(dashboard: DynamicDashboardEntry): void {
        if (!!this.currentDashboard && this.currentDashboard.id === dashboard.id) {
            this.forceRefresh = this.currentDashboard.toStorageString() !== dashboard.toStorageString();
        }
        this.configVisible = false;
        this.selectedDashboard = null;
        this.populateUserDashboards(true);
    }

    private onCommunityChange(): void {
        let communityId: number = null;
        let managementCompanyId: number = null;
        let communityName: string = null;
        if (!!this.selectedCommunity) {
            communityId = this.selectedCommunity.communityId;
            managementCompanyId = this.selectedCommunity.managementCompanyId;
            communityName = this.selectedCommunity.communityName;
        } else if (!!this.selectedContext) {
            communityId = this.selectedContext.communityId;
            managementCompanyId = this.selectedContext.managementCompanyId;
            communityName = this.selectedContext.communityName;
        }
        if (!!this.currentDashboard) {
            this.currentDashboard.dashboardSections.forEach((ds) => {
                ds.dashboardItems.forEach((di) =>
                    di.updateWidgetContext(communityId, managementCompanyId, communityName)
                );
            });
        }
    }

    private onEntityChange(): void {
        if (Utils.notNullAndDefined(this.currentDashboard) && Utils.notNullAndDefined(this.selectedEntity)) {
            this.currentDashboard.dashboardSections.forEach((ds) => {
                ds.dashboardItems.forEach((di) =>
                    di.updateWidgetEntity(this.selectedEntity.entityId, this.selectedEntity.name)
                );
            });
        }
    }

    private getCommunities() {
        this.loadingVisible = true;
        this.communityService.getCommunities().subscribe(
            (data) => {
                this.communities = data;
                this.loadingVisible = false;
                const localContext = this.dashboardService.getLocalContextInfo();
                if (!!localContext && !!localContext.communityId) {
                    this.communityGridBoxValue = [localContext.communityId];
                }
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private getEntities(): void {
        this.loadingVisible = true;
        this.entityService.getEntities().subscribe(
            (results: EntitySelectorResultDto[]) => {
                this.entityList = results.sort((a, b) => a.entityId - b.entityId);
                const selectedEntity = this.ccbHelperService.getSelectedEntity();
                this.entityValue = Utils.notNullAndDefined(selectedEntity) ? selectedEntity.entityId : null;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private populateUserDashboards(clearCache = false): void {
        this.dashboardService.getDashboards(clearCache).subscribe(
            (results) => {
                this.systemDashboards = results.filter((d) => d.isSystem);
                this.userDashboards = results.filter((d) => !d.isSystem);
                this.hasSystemDashboards = this.systemDashboards.length > 0;
                this.hasUserDashboards = this.userDashboards.length > 0;
                this.hasDashboards = this.hasSystemDashboards || this.hasUserDashboards;
                if (this.hasDashboards) {
                    let dashboard = results.find((d) => d.isDefault) || results[0];
                    if (!!this.currentDashboard) {
                        dashboard = results.find((d) => d.id === this.currentDashboard.id) || dashboard;
                    }
                    this.onView(dashboard);
                }
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private computeSectionsSize(): void {
        clearTimeout(this._timerId);
        this._timerId = setTimeout(() => {
            if (!!this.dashboardBox && !!this.dashboardBox.instance) {
                // tslint:disable-next-line:deprecation
                const el: dxElement = this.dashboardBox.instance.element();
                this.viewerWidth = el.clientWidth - 10;
                this.viewerHeight = el.clientHeight - 50;
                if (!!this.currentDashboard) {
                    this.viewerHeight -=
                        this.currentDashboard.viewMode === DashboardViewMode.Carousel &&
                        this.currentDashboard.noOfSections > 1
                            ? 28
                            : 0;
                    let totalWidth = Math.max(this.currentDashboard.totalWidth, this.viewerWidth);
                    let totalHeight = Math.max(
                        this.currentDashboard.totalHeight,
                        this.currentDashboard.viewMode === DashboardViewMode.Carousel ? this.viewerHeight : 0
                    );
                    if (this.currentDashboard.autoFitView) {
                        totalWidth = this.viewerWidth;
                        totalHeight =
                            this.currentDashboard.viewMode === DashboardViewMode.Carousel
                                ? this.viewerHeight
                                : totalHeight;
                    }
                    this.totalWidth = totalWidth;
                    this.totalHeight = totalHeight;
                    this.dashboardTemplates.forEach((dt) => dt.computeTileSize());
                    this.updateScrollbarPreference();
                }
            }
        }, 250);
    }

    private updateScrollbarPreference(): void {
        let scrollbarPreference = this.localStorageService.getItem(
            StorageKeyConstants.userScrollbarKey,
            CCLocalStorageCategory.PREF
        );
        if (!scrollbarPreference) {
            scrollbarPreference = Constants.defaultScrollbarView;
        }
        this.scrollbarPreference = scrollbarPreference;
        this.scrollDirection = this.currentDashboard.autoFitView ? 'vertical' : 'both';
        this.showScrollbar = this.currentDashboard.noOfSections > 0;
        if (this.currentDashboard.viewMode === DashboardViewMode.Carousel && this.currentDashboard.autoFitView) {
            this.showScrollbar = false;
        }
    }
}
